import { AfterViewInit, Directive, EventEmitter, inject, Input, NgZone, OnDestroy, Output } from '@angular/core';
import { CompiereDataGridRequestJSON } from '@compiere-ws/models/compiere-data-json';
import { ActiveTabManagerService } from '@iupics-manager/managers/active-tab-manager/active-tab-manager.service';
import { UICreatorService } from '@iupics-manager/managers/ui-creator/ui-creator.service';
import { IupicsWidget } from '@iupics-manager/models/iupics-widget';
import { SubscriptionsUtils } from '@iupics-util/tools/subscriptions.util';
import { ColumnState } from '@iupics/apiz-grid';
import { WorkspaceService } from '@web-desktop/components/workspace/components/workspace-ui/workspace.service';
import { UrlParamsService } from '@web-desktop/controllers/url-params.service';
import { IupicsMenuType } from '@web-desktop/models/menu-item-ui';
import { interval, Subscription } from 'rxjs';
import { formatNumber, parseDisplayOptions, WidgetDisplayOptions } from './widget-display';

@Directive()
export abstract class AbstractWidgetComponent implements AfterViewInit, OnDestroy {
  static readonly MIN_REFRESH_RATE_SECONDS = 5;

  @Input() isPreview = false;
  @Input() resizeWidgetEmitter = new EventEmitter<any>();
  protected _widget: IupicsWidget;

  @Input()
  set widget(value) {
    // l'utilisation du setter permet de réagir à l'update du widget.
    this._widget = value;
    this.displayOpts = parseDisplayOptions(value?.displayOptions);
    this.updateWidget();
  }

  get widget() {
    return this._widget;
  }

  subscriptions: Subscription[] = [];
  tableName: string;
  isLoading = false;
  // true when the last data call failed (HTTP error): the widget shows a retry link
  loadError = false;
  /** the viewer's role cannot read the source or its columns (not the same as no data) */
  noAccess = false;
  /** last data read (the dashboard card shows "updated ... ago") */
  lastUpdated: Date | null = null;
  /** display options of the widget (chart options, number format, alert direction) */
  displayOpts: WidgetDisplayOptions = {};
  /** set by the dashboard card: false while the card is out of view (the auto-refresh waits) */
  isOnScreen = true;
  @Output() openTabEmitter = new EventEmitter<any>();
  @Output() widgetChanged = new EventEmitter<any>();

  readonly #workspaceService = inject(WorkspaceService);
  readonly #urlParamsService = inject(UrlParamsService);
  readonly #activeTabManager = inject(ActiveTabManagerService);
  readonly #zone = inject(NgZone);

  constructor(protected uiCreator: UICreatorService) {}

  ngAfterViewInit(): void {
    if (!this.isPreview && this.widget.refreshRate > 0) {
      const refreshRateSeconds = Math.max(this.widget.refreshRate, AbstractWidgetComponent.MIN_REFRESH_RATE_SECONDS);
      // the interval ticks outside Angular: only an actual refresh enters the zone
      this.subscriptions.push(
        this.#zone.runOutsideAngular(() =>
          interval(refreshRateSeconds * 1000).subscribe(() => {
            // skip this cycle if the previous refresh is still in flight, or nobody can see it (hidden browser tab)
            if (this.isLoading || document.visibilityState === 'hidden' || !this.isOnScreen) {
              return;
            }
            if (this.#activeTabManager.isDashboardDisplayed()) {
              this.#zone.run(() => this.updateWidget());
            }
          })
        )
      );
    }
  }

  ngOnDestroy(): void {
    SubscriptionsUtils.unsubscribe(...this.subscriptions);
  }

  getDataGridRequest(): CompiereDataGridRequestJSON {
    if (this.widget.gridRequest) {
      return JSON.parse(this.widget.gridRequest);
    } else {
      return null;
    }
  }

  getDataGridState(): ColumnState[] {
    if (this.widget.gridState) {
      return JSON.parse(this.widget.gridState);
    } else {
      return null;
    }
  }

  openTab(event: MouseEvent, item?: { id: string; displayedName: string }): void {
    if (event?.stopPropagation) {
      event.stopPropagation();
    }

    if (!this.isPreview) {
      const id = item?.id;
      let recordId = null;

      if (id) {
        recordId = id.split(',')[1];
        this.zoomAcross(recordId, this.tableName, this.widget.windowId);
      } else if (this.widget.windowId) {
        this.#urlParamsService.updateUrlParams({
          initGridParams: {
            request: this.getDataGridRequest(),
            state: this.getDataGridState(),
          },
        });
        this.#workspaceService.openTargetSearchEmt.emit({
          zoomInfo: null,
          cat: { id: this.widget.windowId },
          source: {
            id: null,
          },
        });
      }
    }
  }

  /* Zoom Across */
  zoomAcross(record_id: any, tableName: string, windowId: number, openNewRecord = false) {
    this.subscriptions.push(
      this.uiCreator.zoomAcross(tableName, tableName + '_ID', record_id ?? null, true, windowId).subscribe({
        next: (dataWs) => {
          if (dataWs && dataWs.length > 0) {
            const zoomInfo = {
              windowId: dataWs[0].Window_ID,
              dataUUID: dataWs[dataWs.length - 1]['Record_ID'],
              record_id: record_id ?? null,
              children: dataWs.length > 1 ? dataWs.splice(0, dataWs.length - 1) : null,
              isInCurrentWindow: true,
            };

            const source = {
              id: null,
              menuType: IupicsMenuType.WINDOW,
            };

            if (record_id) {
              source.id = dataWs[0]?.Record_ID ?? null;
            }

            if (openNewRecord) {
              source.id = 'newRecord';
            }
            this.#urlParamsService.updateUrlParams({ initGridParams: { request: this.getDataGridRequest() ?? null } });
            this.#workspaceService.openTargetSearchEmt.emit({
              zoomInfo: zoomInfo,
              cat: { id: parseInt(dataWs[0].Window_ID, 10) },
              source,
              ctx: null,
            });
          }
        },
      })
    );
  }

  retryLoad(): void {
    this.loadError = false;
    this.updateWidget();
  }

  /**
   * The widget changed only in how it is shown (colours, unit, precision, levels...): the data already read is
   * shown again with it. Default: read again (widgets that keep their data override it).
   */
  applyPresentation(widget: IupicsWidget): void {
    this.widget = widget;
  }

  /** for overrides of applyPresentation: the widget and its options, no new request */
  protected setPresentation(widget: IupicsWidget): void {
    this._widget = widget;
    this.displayOpts = parseDisplayOptions(widget?.displayOptions);
  }

  /** a value as shown (format, currency, unit, decimals of the widget) */
  display(value: number, locale?: string): string {
    return formatNumber(
      value,
      { format: this.displayOpts.format, currency: this.displayOpts.currency, precision: this.widget?.precision, unit: this.widget?.measureUnit },
      locale
    );
  }

  /** data read: card freshness */
  protected markUpdated(): void {
    this.lastUpdated = new Date();
  }

  /** value as shown: coefficient applied, then rounded or truncated to the precision */
  formatValue(raw: unknown): number {
    const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));
    if (!isFinite(parsed)) {
      return 0;
    }
    const value = parsed * (this.widget?.multiplier || 1);
    const precision = this.widget?.precision;
    if (precision == null || precision < 0) {
      return value;
    }
    if (this.widget?.isRounded) {
      return Number(value.toFixed(precision));
    }
    const factor = Math.pow(10, precision);
    return Math.trunc(value * factor) / factor;
  }

  abstract updateWidget(): void;
}
